import React, { useContext, useMemo } from 'react';
import cx from 'classnames';
import { isEmpty, isObject } from 'lodash/lang';
import { get } from 'lodash/object';
import moment from 'moment';

import { ButtonWithIcon } from '../../../../../common/buttons';
import ModalBoxes from '../../../../../common/feedback/ModalBoxes/ModalBoxes';
import Button from '../../../../../common/general/Button';
import Icon from '../../../../../common/general/Icon';
import { DD_SLASH_MMM_SLASH_YYYY } from '../../../../../constants/dateFormat';
import { CLOSED } from '../../../../../constants/ssuStatuses';
import { scBlue100, scYellow10 } from '../../../../../constants/systemColors';
import { MANAGE_LOG_DATA, SIGN_LOGS_GROUP } from '../../../../../constants/userOperations';
import {
  ROLE_CLINICAL_QUALITY_COORDINATOR,
  ROLE_CRA,
  ROLE_EXTERNAL_AUDITOR,
  ROLE_OPERATION_MANAGER,
  ROLE_OPERATIONS_ANALYST,
  ROLE_PRINCIPAL_INVESTIGATOR,
  ROLE_STUDY_MANAGER,
  ROLE_SYSTEM_ADMINISTRATOR
} from '../../../../../constants/userRoles';
import { pendoTrackDefaultSortingChange } from '../../../../../pendo/PendoUtils';
import { userHasAccessTo, userHasRole } from '../../../../../services/auth';
import { useCurrentUser } from '../../../../root/Container/CurrentUserContainer';
import { useCurrentRoute } from '../../../../root/router';
import { resolveReported } from '../../../setup/shared/ElementSetupNew/defaultLogCheckOptions';
import EncounterPatientInfo from '../../shared/EncounterDescNew/EncounterPatientInfo/EncounterPatientInfo';
import InvestigatorFieldsSignatureModal from '../../shared/EncounterDescNew/InvestigatorFieldsSignature/InvestigatorFieldsSignatureModal';
import { LogCheckNew } from '../../shared/EncounterDescNew/LogCheck/LogCheckNew';
import ItemGroupBar from '../../shared/ItemGroupBar/ItemGroupBar';

import LogsHistoryPage from './LogsHistory/LogsHistoryPage';
import { DirectionSection } from './DirectionSection';
import { LogsContext, WithLogsPageContext } from './LogsPageContext';
import {
  isCurrentPageIsForLogReviewCycle,
  isLogAvailableForReviewCycle,
  isLogCanBeLinked,
  isLogWasLinked
} from './LogsPageServices';
import LogsTable from './LogsTable';
import { defaultCellWidth, getColumns } from './LogsTableService';

import './LogsPage.scss';

function LogsPage({ inheritedComponent }) {
  const currentRoute = useCurrentRoute();
  const currentUserRole = useCurrentUser();
  const userCraOrAuditor = [ROLE_CRA, ROLE_EXTERNAL_AUDITOR].includes(currentUserRole.activeRole);
  const {
    studySiteStatus,
    isInActiveProtocol,
    itemGroupData,
    showLogCheck,
    patientInfo,
    logCheck,
    addNewLog,
    logCheckAnswers,
    currentItemGroupName,
    showEncounter,
    currentStudySiteId,
    itemGroupDomainCode,
    onEditRow,
    onEditComment,
    longAnswers,
    signAll,
    sorted,
    setSorted,
    page,
    setPage,
    pageSize,
    setPageSize,
    historyForRow,
    setHistoryForRow,
    groupAssigns,
    directions,
    currentStudyId,
    itemGroupTemplateId,
    currentItemGroupTemplateVersionId,
    currentUserPermissionType,
    linkedSubjectIDs,
    loadLogDataGeneral
  } = useContext(LogsContext);

  const showSignButton = useMemo(() => {
    return (
      userHasAccessTo(SIGN_LOGS_GROUP) &&
      (currentRoute.key.includes('Investigator Worklist.Adverse Event Log Review') ||
        currentRoute.key.includes('Investigator Worklist.Protocol Deviation Log Review'))
    );
  }, [currentRoute]);

  const isAllowedToManageLogs = useMemo(() => {
    if (
      currentRoute.key.includes('Patient Studies.Encounter Details.Logs') ||
      currentRoute.key.includes('Investigator Worklist.Adverse Event Log Review') ||
      currentRoute.key.includes('Investigator Worklist.Protocol Deviation Log Review') ||
      studySiteStatus === CLOSED
    ) {
      return false;
    }
    if (userHasRole(ROLE_SYSTEM_ADMINISTRATOR)) {
      return true;
    }
    if (currentRoute.key.includes('Patient Studies.Encounter Details.Logs')) {
      return userHasAccessTo(MANAGE_LOG_DATA);
    }

    return isInActiveProtocol && userHasAccessTo(MANAGE_LOG_DATA);
  }, [currentRoute, isInActiveProtocol, studySiteStatus]);

  const columns = useMemo(() => {
    return getColumns(
      itemGroupData?.columnList,
      showEncounter,
      itemGroupDomainCode,
      currentStudyId,
      itemGroupTemplateId,
      currentItemGroupTemplateVersionId,
      currentUserPermissionType,
      patientInfo,
      linkedSubjectIDs,
      loadLogDataGeneral
    );
  }, [
    itemGroupTemplateId,
    currentItemGroupTemplateVersionId,
    currentStudyId,
    itemGroupData?.columnList,
    itemGroupDomainCode,
    showEncounter,
    currentUserPermissionType,
    patientInfo,
    linkedSubjectIDs,
    loadLogDataGeneral
  ]);

  const openSignAllReasonModal = () => {
    const rowsWithPIReviewStatus = itemGroupData?.tableData.filter(
      row => row.logRowStatuses[row.rowId] === 'PI_REVIEW'
    );
    const someRowsWithPIReviewStatusHasLinkedSubjects = rowsWithPIReviewStatus.some(row =>
      isLogWasLinked(linkedSubjectIDs, row.rowId)
    );

    let customTitle;
    if (isLogAvailableForReviewCycle(itemGroupDomainCode) && isLogCanBeLinked(itemGroupDomainCode)) {
      customTitle = `I reviewed and approve ${rowsWithPIReviewStatus.length} log entries on ${moment().format(
        DD_SLASH_MMM_SLASH_YYYY
      )}${someRowsWithPIReviewStatusHasLinkedSubjects ? ', including all log entries linked to other subjects' : ''}`;
    }

    ModalBoxes.open({
      component: (
        <InvestigatorFieldsSignatureModal
          customSignFunction={(reason, token) => signAll(reason, token)}
          groupAssignList={groupAssigns}
          customTitle={customTitle}
        />
      ),
      className: 'eds-investigator-fields-sign-modal-box'
    });
  };
  const filteredOnlyReviewLogRows = useMemo(() => {
    if (
      !(
        currentRoute.key.includes('Investigator Worklist.Adverse Event Log Review') ||
        currentRoute.key.includes('Investigator Worklist.Protocol Deviation Log Review')
      )
    ) {
      return itemGroupData?.tableData;
    }
    return itemGroupData?.tableData?.filter(row => row.logRowStatuses[row.rowId] === 'PI_REVIEW') || [];
  }, [itemGroupData?.tableData, currentRoute]);

  const isBulkSign = useMemo(() => {
    return (
      !isEmpty(filteredOnlyReviewLogRows) &&
      (currentRoute.key.includes('Investigator Worklist.Adverse Event Log Review') ||
        currentRoute.key.includes('Investigator Worklist.Protocol Deviation Log Review')) &&
      filteredOnlyReviewLogRows.length > 1
    );
  }, [currentRoute, filteredOnlyReviewLogRows]);

  const logIsAvailableForReviewCycle = useMemo(() => {
    return isLogAvailableForReviewCycle(itemGroupDomainCode);
  }, [itemGroupDomainCode]);

  const currentPageIsForLogWithReviewCycle = useMemo(() => {
    return isCurrentPageIsForLogReviewCycle(currentRoute.name);
  }, [currentRoute.name]);

  const historyColumn = {
    Header: 'History',
    fixed: false,
    sortable: false,
    id: 'History',
    show:
      logIsAvailableForReviewCycle &&
      (userHasRole(ROLE_SYSTEM_ADMINISTRATOR) ||
        userHasRole(ROLE_STUDY_MANAGER) ||
        userHasRole(ROLE_PRINCIPAL_INVESTIGATOR) ||
        userHasRole(ROLE_CLINICAL_QUALITY_COORDINATOR) ||
        userHasRole(ROLE_OPERATIONS_ANALYST) ||
        userHasRole(ROLE_OPERATION_MANAGER)),
    minWidth: 70,
    width: 70,
    Cell: row => {
      return (
        <Icon
          onClick={() => setHistoryForRow(row.original)}
          style={{ fontSize: '25px', color: scBlue100, cursor: 'pointer' }}
        >
          history
        </Icon>
      );
    }
  };

  return (
    <div className={cx('logs-page', { 'full-screen-layout': !showLogCheck })}>
      {patientInfo && !inheritedComponent && (
        <EncounterPatientInfo infoLabel={currentItemGroupName} withLabel={!currentPageIsForLogWithReviewCycle} />
      )}
      {historyForRow && (
        <section className="py-3 px-2 content-section mb-3">
          <LogsHistoryPage />
        </section>
      )}
      <DirectionSection
        directions={directions}
        isLogsPage={currentRoute.key.includes('Patient Studies.Logs')}
        isCraOrAuditor={userCraOrAuditor}
      />
      {!historyForRow && showLogCheck && (
        <section className="log-check">
          {logCheck && logCheck.rows && (
            <LogCheckNew
              logCheckOptions={get(logCheck, 'rows') || []}
              logCheckAnswers={logCheckAnswers}
              addNewLog={addNewLog}
              studySiteId={currentStudySiteId}
              itemGroupDomainCode={itemGroupDomainCode}
              patientInfo={patientInfo}
            />
          )}
        </section>
      )}
      {!historyForRow && (
        <section className="p-3 content-section mb-3">
          {logIsAvailableForReviewCycle && currentPageIsForLogWithReviewCycle && (
            <ItemGroupBar className="item-group-bar-review" name={currentItemGroupName} />
          )}
          {isAllowedToManageLogs && (
            <div>
              {logIsAvailableForReviewCycle ? (
                <Button size={'h28'} className={'mr-2 mb-2'} onClick={addNewLog}>
                  {resolveReported(itemGroupDomainCode)}
                </Button>
              ) : (
                <ButtonWithIcon onClick={addNewLog}>Add</ButtonWithIcon>
              )}
            </div>
          )}
          {!isEmpty(itemGroupData) && (
            <LogsTable
              columns={[historyColumn, ...columns]}
              data={filteredOnlyReviewLogRows}
              onEditRow={onEditRow}
              longAnswers={longAnswers}
              onPageSizeChange={setPageSize}
              onPageChange={setPage}
              onSortedChange={sorted => {
                pendoTrackDefaultSortingChange(sorted);
                setSorted(sorted);
              }}
              pageSize={pageSize}
              page={page}
              defaultSorted={sorted}
              getTdProps={(state, rowInfo, column) => {
                let props = {};
                const columnId = column.id;
                if (
                  rowInfo &&
                  (rowInfo.row[columnId]?.question === 'Comment' ||
                    rowInfo.row[columnId]?.itemGroupQuestionId === 'COVAL')
                ) {
                  let commentValue = get(rowInfo, `row[${columnId}].answer`, '');
                  props.onClick = event => {
                    onEditComment(rowInfo, commentValue, event);
                  };
                }
                if (rowInfo && columnId !== 'Comment' && columnId !== 'History' && columnId !== 'Link PD') {
                  props.onClick = () => onEditRow(rowInfo.original);
                }
                if (
                  rowInfo &&
                  isObject(rowInfo.row) &&
                  rowInfo?.row[columnId]?.answer?.length > 1 &&
                  rowInfo?.row[columnId]?.attributeType !== 'file' &&
                  longAnswers.includes(rowInfo.row[columnId]?.name)
                ) {
                  props = { ...props, className: 'text-left' };
                }
                if (rowInfo?.original?.changedAnswers && rowInfo.original.changedAnswers.length) {
                  const column = rowInfo.row[columnId];
                  if (rowInfo.original.changedAnswers.includes(column?.attributeIdentifier)) {
                    props.style = { backgroundColor: scYellow10 };
                  }
                }
                return props;
              }}
              showPagination={true}
              defaultCellWidth={defaultCellWidth}
            />
          )}
          {showSignButton && (
            <Button className="sign-button" size="h28" priority="high" onClick={() => openSignAllReasonModal()}>
              {isBulkSign ? 'Bulk Sign' : 'Sign'}
            </Button>
          )}
        </section>
      )}
    </div>
  );
}

export default WithLogsPageContext(LogsPage);
